<template lang="html">
  <section class="game">
    <!-- TODO: Make this preload the current hand first for less delay -->
    <CardPreloader
      v-if="dealerHand && playerHands"
      :dealer-hand="dealerHand"
      :player-hand="playerHands[0]"
      @initial-cards-preloaded="initialCardsPreloaded = true"
    />

    <GameStats />

    <section v-if="initialCardsPreloaded" class="game-inner">
      <div class="card-area">
        <template>
          <Hand
            v-if="dealerHand"
            class="dealer-hand"
            :hand="dealerHand"
            reverse
          />

          <ol v-if="playerHands" class="player-hands">
            <li
              v-for="hand in playerHands"
              :key="hand.id"
              class="player-hand-item"
            >
              <Hand
                :hand="hand"
                :unfocused="
                  playerHands.length > 1 &&
                  focusedHand &&
                  focusedHand.id !== hand.id
                "
              />
            </li>
          </ol>
        </template>
      </div>

      <div class="dashboard-area">
        <div class="message-area">
          <p class="message">{{ gameMessage }}</p>
        </div>

        <ol class="action-buttons primary">
          <template v-if="step === GameStep.WaitingForPlayInput">
            <li class="action-button-item">
              <button data-action="s" class="action-button">
                Stand<template v-if="shouldShowHotkeyTip"> (S)</template>
              </button>
            </li>
            <li class="action-button-item">
              <button data-action="h" class="action-button">
                Hit<template v-if="shouldShowHotkeyTip"> (H)</template>
              </button>
            </li>
          </template>
          <template v-else-if="step === GameStep.WaitingForInsuranceInput">
            <li class="action-button-item">
              <button data-action="n" class="action-button">
                No<template v-if="shouldShowHotkeyTip"> (N)</template>
              </button>
            </li>
            <li class="action-button-item">
              <button data-action="y" class="action-button">
                Yes<template v-if="shouldShowHotkeyTip"> (Y)</template>
              </button>
            </li>
          </template>
          <template v-else>
            <li class="action-button-item">
              <button data-action="d" class="action-button primary">
                Deal<template v-if="shouldShowHotkeyTip">
                  (press any key)</template
                >
              </button>
            </li>
          </template>
        </ol>

        <ol class="action-buttons secondary">
          <template v-if="step === GameStep.WaitingForPlayInput">
            <li
              v-if="focusedHand && focusedHand.firstMove"
              class="action-button-item"
            >
              <button data-action="d" class="action-button small">
                Double<template v-if="shouldShowHotkeyTip"> (D)</template>
              </button>
            </li>
            <li v-if="allowLateSurrender" class="action-button-item">
              <button data-action="r" class="action-button small">
                Surrender<template v-if="shouldShowHotkeyTip"> (R)</template>
              </button>
            </li>
            <li v-if="allowSplit" class="action-button-item">
              <button data-action="p" class="action-button small">
                Split<template v-if="shouldShowHotkeyTip"> (P)</template>
              </button>
            </li>
            <li class="action-button-item">
              <button
                class="action-button small"
                @click="$store.dispatch('clickShowStatsButton')"
              >
                Toggle Stats
              </button>
            </li>
          </template>
        </ol>

        <ul class="game-rules-list">
          <li class="game-rule">
            {{ deckCountName }}, dealer {{ soft17Setting }} soft 17
            (<router-link :to="{ name: 'settings' }">settings</router-link>)
          </li>

          <li
            v-if="settings.mode && settings.mode !== 'default'"
            class="game-rule"
          >
            Game mode:
            <b
              v-tooltip="{
                content: gameModeDetails[settings.mode].detail,
                offset: 20,
              }"
              >{{ gameModeDetails[settings.mode].name }}</b
            >
          </li>
        </ul>
      </div>

      <AppFooter />
    </section>

    <AppSpinner v-else />
  </section>
</template>

<script>
import {
  Game,
  PlayerInputReader,
  GameStep,
  GameMode,
  Event,
  gameSettings,
} from '@blackjacktrainer/blackjack-simulator';
import { mapState, mapGetters } from 'vuex';
import { capitalize } from 'lodash';

import Hand from '@/components/Hand';
import CardPreloader from '@/components/CardPreloader';
import AppSpinner from '@/components/AppSpinner';
import GameStats from '@/components/GameStats';
import AppFooter from '@/components/AppFooter';
import mobileDetection from '@/mixins/mobile-detection';
import { modelClassFromEntityName } from '@/utils/models';
import formatJsonApiData from '@/utils/format-json-api-data';
import { gameModeDetails } from '@/constants';

const ANIMATION_DELAY_MS = 150;

export default {
  name: 'Game',

  components: {
    Hand,
    CardPreloader,
    AppSpinner,
    GameStats,
    AppFooter,
  },

  mixins: [mobileDetection],

  data() {
    return {
      // NOTE: These need to be non-dynamic strings to get imported correctly.
      charts: {
        '1d-s17': require('@/assets/images/charts/1d-s17.gif'),
        '1d-h17': require('@/assets/images/charts/1d-h17.gif'),
        '2d-s17': require('@/assets/images/charts/2d-s17.gif'),
        '2d-h17': require('@/assets/images/charts/2d-h17.gif'),
        'multi-s17': require('@/assets/images/charts/multi-s17.gif'),
        'multi-h17': require('@/assets/images/charts/multi-h17.gif'),
      },

      gameModeDetails,
      initialCardsPreloaded: false,
      GameStep,
    };
  },

  computed: {
    ...mapState(['dealer', 'playCorrection', 'step']),
    ...mapGetters([
      'currentUser',
      'dealerHand',
      'playerHands',
      'handWinner',
      'deckCountName',
      'settings',
      'focusedHand',
    ]),

    gameMessage() {
      if (this.step === GameStep.WaitingForInsuranceInput) {
        return 'Insurance?';
      }

      // TODO: Align results with hands.
      const result = this.playerHands
        .map((hand) => this.gameMessageForHand(hand))
        .filter((hand) => !!hand)
        .join(', ');

      return result;
    },

    allowLateSurrender() {
      return this.focusedHand?.firstMove && this.settings.allowLateSurrender;
    },

    allowSplit() {
      return (
        this.focusedHand?.hasPairs &&
        this.playerHands.length < this.settings.maxHandsAllowed
      );
    },

    shouldShowHotkeyTip() {
      return this.isGtMobile;
    },

    chartImagePath() {
      if (!this.settings.deckCount) {
        return null;
      }

      let deckPrefix = 'multi';
      if (this.settings.deckCount === '1' || this.settings.deckCount === '2') {
        deckPrefix = `${this.settings.deckCount}d`;
      }

      const soft17 = this.settings.hitSoft17 ? 'h' : 's';
      const imageName = `${deckPrefix}-${soft17}17`;

      return this.charts[imageName];
    },

    soft17Setting() {
      return this.settings.hitSoft17 ? 'hits' : 'stays';
    },
  },

  watch: {
    'settings.checkDeviations'(checkDeviations) {
      if (this.game && checkDeviations != null) {
        this.game.updateSettings({ checkDeviations });
      }
    },

    'settings.allowLateSurrender'(allowLateSurrender) {
      if (this.game && allowLateSurrender != null) {
        this.game.updateSettings({ allowLateSurrender });
      }
    },

    'settings.autoDeclineInsurance'(autoDeclineInsurance) {
      if (this.game && autoDeclineInsurance != null) {
        this.game.updateSettings({ autoDeclineInsurance });
      }
    },

    'settings.hitSoft17'(hitSoft17) {
      if (this.game && hitSoft17 != null) {
        this.game.updateSettings({ hitSoft17 });
      }
    },

    'settings.deckCount'(deckCount) {
      if (this.game && deckCount != null) {
        this.game.updateSettings({ deckCount });
        this.game.resetState();
      }
    },

    'settings.mode'(mode) {
      if (this.game && mode) {
        if (mode === 'deviations') {
          this.$store.dispatch('setupDeviationsGameMode');
        }

        this.game.updateSettings({ mode: GameMode[capitalize(mode)] });
        this.game.resetState();
      }
    },

    playCorrection(playCorrection) {
      if (!playCorrection) {
        return;
      }

      // TODO: Add a chart for deviations.
      const isDeviationCorrection = /illustrious|fab/i.test(playCorrection);

      if (this.chartImagePath && !isDeviationCorrection) {
        // TODO: Can we make this DRY with `RemoteLink`?
        playCorrection = `<a href="${this.chartImagePath}" target="_blank" rel="noopener noreferrer">${playCorrection}</a>`;
      }

      this.$toasted.show(playCorrection, { type: 'info' });
    },
  },

  async mounted() {
    this.game = await this.setupGame();
    this.startGame();
  },

  activated() {
    if (this.settings.mode === 'deviations') {
      this.$store.dispatch('setupDeviationsGameMode');
    }
  },

  methods: {
    gameMessageForHand(hand) {
      switch (this.handWinner[hand.id]) {
        case 'player':
          return hand.blackjack ? 'Blackjack! Player wins' : 'Player wins';
        case 'dealer':
          return this.dealerHand.blackjack
            ? 'Dealer wins (blackjack)'
            : 'Dealer wins';
        case 'push':
          return 'Push';
      }
    },

    shouldAnimate(attributeName, value) {
      const prevDealer = this.prevDealer || this.dealerHand;

      // Adds an animation delay while updating the dealer's cards.
      if (
        attributeName === 'dealer' &&
        value.hands[0].cards.length > 0 &&
        prevDealer.hands[0].cards.length > 0
      ) {
        const isFlip =
          value.hands[0].cards[0].showingFace &&
          !prevDealer.hands[0].cards[0].showingFace;

        const isDraw =
          value.hands[0].cards.length > prevDealer.hands[0].cards.length;

        if (isFlip) {
          return 'flip';
        }

        if (isDraw) {
          return 'draw';
        }

        return null;
      }

      // HACK: Prevents UI flicker while dealing a new hand.
      if (
        (attributeName === 'player' || attributeName === 'dealer') &&
        value.hands[0].cards.length < 2
      ) {
        return null;
      }

      return false;
    },

    clearAnimation() {
      for (let id of this.animationIntervalIds) {
        clearTimeout(id);
      }

      this.animationIntervalIds = [];
    },

    queueAnimation(action, { leading = true } = {}) {
      const now = Date.now();

      let delay;
      if (leading) {
        const deltaSinceLastAnimation = now - this.lastQueuedAnimationTime;
        delay = Math.max(0, ANIMATION_DELAY_MS - deltaSinceLastAnimation);
      } else {
        delay = ANIMATION_DELAY_MS;
      }

      this.lastQueuedAnimationTime = now + delay;

      if (delay === 0) {
        action();
      } else {
        this.animationIntervalIds.push(setTimeout(action, delay));
      }
    },

    async setupGame() {
      await this.$auth.whenCurrentUserReady();

      const settings = Object.assign({}, this.settings);
      settings.mode = GameMode[capitalize(settings.mode)];
      const game = new Game(settings);

      this.$store.dispatch('initializeGame', gameSettings);

      this.animationIntervalIds = [];
      this.lastQueuedAnimationTime = 0;

      game.on(Event.Change, (attributeName, value) => {
        const animationType = this.shouldAnimate(attributeName, value);

        if (attributeName === 'dealer') {
          this.prevDealer = value;
        }

        if (animationType === null) {
          return;
        }

        if (animationType === false) {
          this.$store.dispatch('updateGame', { attributeName, value });
          return;
        }

        this.queueAnimation(
          () => {
            this.$store.dispatch('updateGame', { attributeName, value });
          },
          { leading: animationType === 'draw' }
        );
      });

      game.on(Event.Shuffle, () => {
        if (this.settings.mode === 'default') {
          this.$toasted.show('End of shoe, cards shuffled!', { type: 'info' });
        }
      });

      game.on(Event.CreateRecord, (entityName, data) => {
        if (!this.currentUser) {
          return;
        }

        const modelClass = modelClassFromEntityName(entityName);
        modelClass
          .api()
          .post(`/${modelClass.entity}`, formatJsonApiData(modelClass, data));
      });

      return game;
    },

    stepGame(game, playerInputReader, input) {
      const step = game.step(input);

      if (
        ![
          GameStep.WaitingForPlayInput,
          GameStep.WaitingForInsuranceInput,
          GameStep.WaitingForNewGameInput,
        ].includes(step)
      ) {
        return Promise.resolve();
      }

      return new Promise((resolve) =>
        playerInputReader.readInput((key) => {
          this.clearAnimation();
          return this.$route.name === 'game' ? resolve(key) : resolve(null);
        })
      );
    },

    async startGame() {
      this.game.betAmount = 10 * 100;

      const playerInputReader = new PlayerInputReader();
      let input;

      while (true) {
        input = await this.stepGame(this.game, playerInputReader, input);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/mixins/unstyled-list';
@import '@/styles/mixins/button';
@import '@/styles/mixins/app-width';

.game {
  display: flex;
  position: relative;
  padding-bottom: 0;
}

.game-inner {
  @include app-width;

  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  flex-grow: 1;
  justify-content: center;
}

.game-inner,
.card-area {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.card-area {
  width: 100%;
  justify-content: space-between;
}

.player-hands,
.action-buttons {
  @include unstyled-list;
}

.dealer-hand {
  margin-bottom: 0.5em;
}

.player-hands {
  margin-top: 0.5em;
}

.player-hand-item {
  flex-grow: 1;
  margin-left: 5em;

  &:first-child {
    margin-left: 0;
  }
}

.message-area {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: map-get($app, 'spacing') * 3;
}

.message {
  margin: 0;
}

.action-buttons {
  justify-content: center;
  margin-bottom: map-get($app, 'spacing') / 2;

  &.secondary {
    min-height: 1.925em;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
}

.action-button-item {
  margin-right: 0.5em;

  &:last-child {
    margin-right: 0;
  }
}

.action-button {
  @include button;

  // Prevent zoom in on double tap.
  touch-action: manipulation;
}

.action-buttons.primary .action-button {
  // Ensure the "hit" button is as big as the stand button.
  min-width: 4.3em;
}

.game-rules-list {
  @include unstyled-list;

  flex-direction: column;
  text-align: center;
  font-size: 0.9em;
  margin-top: map-get($app, 'spacing');
}

.game-rule {
  margin-bottom: 0.25em;

  &:last-child {
    margin-bottom: 0;
  }
}

@media all and (min-width: map-get($breakpoints, 'phone')) {
  .game-inner {
    padding-top: map-get($app, 'spacing');
  }

  .game-rules-list {
    margin-top: map-get($app, 'spacing') * 2;
  }

  .dealer-hand {
    margin-bottom: 1em;
  }

  .player-hands {
    margin-top: 1em;
  }

  .message-area {
    min-height: map-get($app, 'spacing') * 4;
  }

  .action-buttons.primary .action-button {
    // Ensure the "hit" button is as big as the stand button.
    min-width: 6em;
  }
}
</style>
